/// <reference types="w3c-web-nfc" />
import { AsyncPipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, first, map, Observable, withLatestFrom } from 'rxjs';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaFormComponent } from '@priva/components/form';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaFormSectionComponent } from '@priva/components/form-section';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaSpinnerComponent } from '@priva/components/spinner';
import { PrivaTitleComponent } from '@priva/components/title';
import { PrivaLocalizationModule } from '@priva/localization';

import { ScanNfcControllerComponent } from 'app/features/nfc/components';
import { NfcReadEvent } from 'app/features/nfc/models';
import { scanQrBtnIconClass } from 'app/features/scan-flow/utils';
import { selectApiVersion } from 'app/state/api-config/api-config.selectors';
import { isAuthorized } from 'app/state/user-session/user-session.utils';

import { AuthenticatedUser, SignInError, SigninStore } from './signin.store';

@Component({
    selector: 'app-signin-form',
    templateUrl: './signin-form.component.html',
    styleUrl: './signin-form.component.scss',
    providers: [SigninStore],
    imports: [
        AsyncPipe,
        ReactiveFormsModule,
        PrivaButtonComponent,
        PrivaFormComponent,
        PrivaFormSectionComponent,
        PrivaFormGroupComponent,
        PrivaInputComponent,
        PrivaLocalizationModule,
        PrivaSpinnerComponent,
        PrivaTitleComponent,
        ScanNfcControllerComponent,
    ],
})
export class SigninFormComponent implements AfterViewInit {
    private destroyRef = inject(DestroyRef);
    private signinStore = inject(SigninStore);
    private store = inject(Store);

    forceScanIfPossible = input(undefined, {
        transform: (value: boolean) => {
            this.signinStore.updateForceScanIfPossible(value);
        },
    });
    requiredRoles = input<string[] | undefined | null>([]);
    title = input('');

    authorized = output<AuthenticatedUser>();

    readonly employeeNrInput = viewChild('employeeNrInput', { read: ElementRef });

    readonly pinInput = viewChild('pinInput', { read: ElementRef });

    readonly scanButton = viewChild('scanButton', { read: ElementRef });

    authenticating$: Observable<boolean>;

    authenticationError$: Observable<SignInError | undefined>;

    employeeControl = new FormControl<number | null>(null, [Validators.required]);
    isManualInputForbidden$ = this.signinStore.isManualInputForbidden$;
    hideReaderMalfunctionError$ = this.isManualInputForbidden$.pipe(
        map((isManualInputForbidden) => !isManualInputForbidden),
    );
    pinControl = new FormControl<string>('', [Validators.required]);

    formGroup = new FormGroup({
        employeeNumber: this.employeeControl,
        pin: this.pinControl,
    });

    isScanBarcodeEnabled$ = this.signinStore.inputMode$.pipe(
        withLatestFrom(this.signinStore.appMode$),
        map(([inputMode, appMode]) => ['barcode'].includes(inputMode) && appMode !== 'multi-user'),
    );

    isNfcReaderAvailable$ = this.signinStore.inputMode$.pipe(map((inputMode) => ['nfc'].includes(inputMode)));

    scanQrBtnIconClass = scanQrBtnIconClass;

    constructor() {
        this.authenticating$ = this.signinStore.authenticating$;
        this.authenticationError$ = this.signinStore.authenticationError$;
        this.signinStore.authenticatedUser$
            .pipe(takeUntilDestroyed(this.destroyRef), withLatestFrom(this.store.select(selectApiVersion)))
            .subscribe(([{ credentials, userInfo, personalUserPreferences }, apiVersion]) => {
                const requiredRoles = this.requiredRoles() ?? [];
                if (isAuthorized({ userInfo, requiredRoles, apiVersion })) {
                    this.authorized.emit({ credentials, userInfo, personalUserPreferences });
                } else {
                    const message =
                        requiredRoles.length > 1
                            ? `ERROR.TITLE.ROLES_NOT_ASSIGNED`
                            : `ERROR.TITLE.SINGLE_ROLE_NOT_ASSIGNED`;

                    this.formGroup.setErrors({
                        authorisation: message,
                        requiredRoles: requiredRoles.join(', '),
                    });
                }
            });

        this.authenticationError$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((authenticationError) => {
                this.formGroup.setErrors({ authentication: authenticationError });
            });

        this.signinStore.scanResult$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((scanResult) => scanResult?.employeeNumber ?? 0),
                filter((employeeNumber) => !!employeeNumber),
            )
            .subscribe((employeeNumber) => {
                if (employeeNumber) {
                    this.formGroup.setValue({ employeeNumber, pin: '' });
                    this.pinControl.setErrors(null);
                    this.focusOnPinInput();
                }
            });
    }

    tryCompleteEmployeeNrInput() {
        this.formGroup.controls.employeeNumber.markAsTouched();
        if (this.formGroup.controls.employeeNumber.valid) {
            this.focusOnPinInput();
        }
    }

    private focusOnEmployeeNrInput() {
        return this.employeeNrInput()?.nativeElement?.focus();
    }

    private focusOnScanButton() {
        return this.scanButton()?.nativeElement?.focus();
    }

    focusOnPinInput() {
        return this.pinInput()?.nativeElement?.focus();
    }

    ngAfterViewInit(): void {
        this.isManualInputForbidden$.pipe(first()).subscribe((isManualInputForbidden) => {
            if (isManualInputForbidden) {
                this.focusOnScanButton();
            } else {
                this.focusOnEmployeeNrInput();
            }
        });
    }

    authenticate() {
        const employeeNumber = this.employeeControl.value;
        const pin = this.pinControl.value?.toString() ?? '';

        if (employeeNumber && pin) {
            this.signinStore.signIn({ employeeNumber, pin });
        }
    }

    protected readonly scan = this.signinStore.scan;

    canAuthenticate() {
        return this.formGroup.valid || this.formGroup.errors?.authentication;
    }

    onNfcRead({ readText, error }: NfcReadEvent) {
        if (!error) this.signinStore.processScannedCode({ code: readText });
    }

    authenticateIfPossible() {
        if (this.canAuthenticate()) this.authenticate();
    }

    scanBarcodeIfManualInputForbidden() {
        this.signinStore.scanBarcodeIfManualInputForbidden();
    }
}
